// src/components/context/AuthContext.js

/* global gapi:false */

import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    GoogleAuthProvider,
    signInWithPopup,
    deleteUser,
    setPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import { initializeFirebase } from '../../firebase';
import api, { resetCsrfToken } from '../../utils/api';
import { Capacitor } from '@capacitor/core';
// ⭐ Web: cookie-only, nothing stored in localStorage — the httpOnly
// `authToken` cookie is sent automatically (api.js withCredentials: true).
// Native: unchanged — localStorage + Authorization header, since Capacitor
// apps have no first-party domain for a cookie to attach to. This mirrors
// the same isNativePlatform() branch already used in api.js and
// SocketProvider.js.
const isNative = Capacitor.isNativePlatform();

const AuthContext = createContext(null);

// тнР HELPER: Safe User Parsing from LocalStorage
const getStoredUser = () => {
    try {
        const stored = localStorage.getItem('user');
        return stored ? JSON.parse(stored) : null;
    } catch (e) {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    // Hydrate User IMMEDIATELY from Storage (cached profile only — not the token)
    const [user, setUser] = useState(getStoredUser);
    
    // Optimistic Loading State — based on cached user only now, not a localStorage token
    const [loading, setLoading] = useState(!getStoredUser());
    
    // Native: hydrate from localStorage on launch, as before.
    // Web: always starts null — the httpOnly cookie carries the session
    // instead, and a page reload re-establishes `user`/`token` via
    // Firebase's persisted session -> fetchAndSetUser(), same path as before.
    const [token, setToken] = useState(
        isNative ? localStorage.getItem('token') : null
    );
    const [auth, setAuth] = useState(null);
    const isSigningUp = useRef(false);

    // ⭐ NEW: Ref that always holds the latest `user` value, safe to read inside
    // the onAuthStateChanged closure below (which is set up once on mount).
    const userRef = useRef(user);
    useEffect(() => {
        userRef.current = user;
    }, [user]);

    // Helpers for offline cleanup
    const ACTIVE_JOURNEY_KEY = 'activeJourney';
    const PENDING_JOBS_KEY = 'pendingOfflineJobUpdates';
    
    const clearJobLocalState = useCallback(() => {
        localStorage.removeItem(ACTIVE_JOURNEY_KEY);
        localStorage.removeItem(PENDING_JOBS_KEY);
    }, []);

    // Robust LocalStorage Sync
    const setAndStoreUser = useCallback((userData) => {
        if (userData) {
            localStorage.setItem('user', JSON.stringify(userData));
            setUser(userData);
        } else {
            localStorage.removeItem('user');
            setUser(null);
        }
    }, []);

    const fetchAndSetUser = useCallback(async (firebaseUser, recaptchaToken) => {
        if (!firebaseUser) {
            // "Sticky Session" Logic
            // If Firebase says "No User" (which happens on iOS wake), we DO NOT log out.
            // ⭐ CHANGED: We now trust the cached/in-memory user state instead of a
            // localStorage token. If the session is truly dead, the API will reject
            // requests via the cookie (401) and the normal 401 handling in api.js kicks in.
            if (!userRef.current) {
                setAndStoreUser(null);
                setToken(null);
            }
            return null;
        }

        try {
            // 'false' allows using the cached token if valid.
            // This prevents the app from crashing due to "Network Error" when waking up on iOS 
            // before the network stack is fully active.
            const idToken = await firebaseUser.getIdToken(false);
            
            const payload = { idToken, recaptchaToken };
            const response = await api.post('/auth/firebase-login', payload);
            
            if (response.data.user) {
                const backendToken = response.data.token;
                let mongooseUser = response.data.user; // Use 'let' so we can update it
                
                // тнР NEW: Email Synchronisation Check тнР
                // If Firebase email differs from the DB email, the user clicked a verification link.
                if (firebaseUser.email && firebaseUser.email !== mongooseUser.email) {
                    try {
                        // Tell the backend to update the profile email.
                        // No manual Authorization header — the __session cookie
                        // was already set by this same login response, so `api`
                        // authenticates this call the same way as every other
                        // request on web. (Native still isn't affected: it
                        // authenticates via api.js's header interceptor, same
                        // as always.)
                        await api.put('/auth/profile', { email: firebaseUser.email });
                        console.log('[AUTH] Synchronised new verified email to database:', firebaseUser.email);
                        // Update the local object so the UI reflects the change instantly
                        mongooseUser.email = firebaseUser.email;
                    } catch (syncErr) {
                        console.error('[AUTH] Failed to sync verified email to database', syncErr);
                    }
                }
                
                // Refresh our local data with the server's fresh data.
                // ⭐ Native: store the token BEFORE setting the user, same race-condition
                // fix as before — every context (Settings, Currency, Chat, AppContent)
                // fires its fetches the instant `user` changes, and on native that read
                // comes from localStorage, so it must already be written.
                // Web: nothing to store — the httpOnly cookie was already set by the
                // server response itself, before this code even runs.
                setToken(isNative ? backendToken : null);
                if (isNative) localStorage.setItem('token', backendToken);

                // ⭐ FIX: Only wipe unsynced offline job data (pending clock-in/out
                // snapshots, active journey) when this is genuinely a DIFFERENT
                // account logging in — not when Firebase silently restores the same
                // staff member's session on a cold app start/relaunch. This used to
                // run unconditionally here, which meant every relaunch (including
                // offline, right after a staff member clocked in) deleted their own
                // not-yet-synced job/clock-in state before StaffJobCard ever got a
                // chance to restore it — making it look like they'd been clocked out.
                const isDifferentAccount = userRef.current && userRef.current._id !== mongooseUser._id;
                if (isDifferentAccount) {
                    clearJobLocalState();
                }

                // ⭐ FIX: A genuine login (nobody was logged in, or a different account
                // was) must not keep using a CSRF token fetched for the previous
                // session. Previously the cached token was only cleared on logout(),
                // so a token from before this login could still be in memory and
                // every save straight after logging in was rejected as stale until
                // the page was refreshed. Clearing it here means the next save
                // fetches a fresh token bound to THIS session. Background re-syncs of
                // the same account are left alone (their token is still valid).
                if (!userRef.current || isDifferentAccount) {
                    resetCsrfToken();
                }

                setAndStoreUser(mongooseUser); // Now uses the potentially updated user
                
                return response.data;
            } 
            else if (response.data.twoFARequired) {
                return response.data;
            }
            else {
                // Backend rejected the user
                if (auth) await signOut(auth);
                return null;
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            console.error(`[AUTH_STATE] Sync failed: ${errorMessage}`);

            // If sync fails (network error), KEEP THE SESSION ALIVE.
            // ⭐ CHANGED: We assume the cookie session is still good, using the
            // cached user as our signal instead of a localStorage token.
            if (!userRef.current && auth) {
                await signOut(auth);
            }
            
            // Only throw if this was a MANUAL login attempt (recaptcha present).
            // If this was a background refresh, suppress the error so the app doesn't crash/logout.
            if (recaptchaToken) throw error;
            
            return null;
        }
    }, [auth, clearJobLocalState, setAndStoreUser]);
    
    const updateUser = (newUserObject, newToken = null) => {
        if (newUserObject) setAndStoreUser(newUserObject);
        if (newToken) {
            // Native only — same rule as fetchAndSetUser/signup/complete2FALogin.
            // This function is called from elsewhere in the app (e.g. after a
            // Stripe confirmation or a settings save) with a real backend
            // token as the second argument; without this guard it was writing
            // that JWT straight into localStorage on web too, undoing the
            // cookie-only migration for anyone who hit those call sites.
            setToken(isNative ? newToken : null);
            if (isNative) localStorage.setItem('token', newToken);
        }
    };

    const refreshUser = async () => {
        if (auth?.currentUser) {
            return await fetchAndSetUser(auth.currentUser, null); 
        }
        return null;
    };
    
    useEffect(() => {
        let unsubscribe = () => {};

        const setupAuth = async () => {
            try {
                const { auth: firebaseAuth } = await initializeFirebase();

                // 1. Force Local Persistence for PWA
                await setPersistence(firebaseAuth, browserLocalPersistence);
                setAuth(firebaseAuth);

                // ⭐ REMOVED: manual localStorage token → header setup.
                // The cookie is sent automatically; nothing to do here.

                // 3. Listen for Firebase Changes
                unsubscribe = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
                    console.log(`[AUTH] State Changed. Firebase User: ${firebaseUser ? 'Yes' : 'No'}`);

                    // ⭐ REMOVED: unconditional clearJobLocalState() that used to run here
                    // on every single onAuthStateChanged callback — including a normal
                    // cold-start session restore for the SAME staff member. That wiped
                    // their pending/unsynced clock-in and active-journey data before
                    // StaffJobCard ever got a chance to restore it. Job data is now only
                    // cleared on a genuine account switch (see fetchAndSetUser) or an
                    // explicit logout() call, both below.

                    // тнР MODIFIED: Wrapped in try/catch/finally to ensure loading state resolves
                    try {
                        if (firebaseUser) {
                            if (isSigningUp.current) {
                                // Don't interfere with signup process
                                return;
                            }
                            await fetchAndSetUser(firebaseUser, null);
                        } else {
                            // Sticky Session Logic
                            // ⭐ CHANGED: Use the cached/in-memory user as the signal instead
                            // of a localStorage token. The cookie-based session will be
                            // validated by the API itself (401 → logout) if it's truly invalid.
                            if (userRef.current) {
                                console.log('[AUTH] Firebase disconnected, but a cached user session exists. Staying logged in.');
                                // Do nothing. Keep user state as is.
                            } else {
                                // Only if there's no cached user do we confirm logout
                                console.log('[AUTH] No cached user and no Firebase user. Confirmed Logged Out.');
                                setAndStoreUser(null);
                                setToken(null);
                            }
                        }
                    } catch (err) {
                        console.error('[AUTH] Critical error during state change:', err);
                    } finally {
                        setLoading(false);
                    }
                });
            } catch (error) {
                console.error("Firebase Init Error:", error);
                setLoading(false);
            }
        };

        setupAuth();
        return () => unsubscribe();
    }, [fetchAndSetUser, clearJobLocalState, setAndStoreUser]); 

    // --- Standard Auth Functions ---

    const login = async (email, password, recaptchaToken) => {
        if (!auth) throw new Error("Auth not ready");
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        return await fetchAndSetUser(userCredential.user, recaptchaToken);
    };
    
    const complete2FALogin = async (userId, twoFAToken) => {
        const response = await api.post('/auth/verify-2fa', { userId, twoFAToken });
        const { token: backendToken, user: mongooseUser } = response.data;
        
        // ⭐ FIX: fresh session after 2FA - drop any CSRF token from before it
        resetCsrfToken();

        // ⭐ Native only — see fetchAndSetUser for why. Web relies on the
        // cookie the server already set on this same response.
        setToken(isNative ? backendToken : null);
        if (isNative) localStorage.setItem('token', backendToken);
        setAndStoreUser(mongooseUser);
        return mongooseUser;
    };

    const signup = async (email, password, companyName, contactPersonName, contactPhone, recaptchaToken, wantsToAffiliate, referredByCode, referralOfferType, wantsToFranchise, franchisorCode) => {
        isSigningUp.current = true; 
        if (!auth) throw new Error("Auth not ready");
    
        let firebaseUser = null;
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            firebaseUser = userCredential.user;
            
            const response = await api.post('/auth/register-with-firebase', {
                firebaseUid: firebaseUser.uid, 
                email, companyName, contactPersonName, contactPhone, recaptchaToken,
                isAffiliate: wantsToAffiliate, 
                referredByCode: referredByCode || null,
                referralOfferType: referralOfferType || 'split',
                isFranchisor: wantsToFranchise, 
                franchisorCode: franchisorCode || null 
            });

            const { token: backendToken, user: mongooseUser } = response.data;

            // ⭐ FIX: brand new session after signup - drop any CSRF token from before it
            resetCsrfToken();
            
            // ⭐ Native only — see fetchAndSetUser for why. Web relies on the
            // cookie the server already set on this same response.
            setToken(isNative ? backendToken : null);
            if (isNative) localStorage.setItem('token', backendToken);
            setAndStoreUser(mongooseUser);
            return mongooseUser;
            
        } catch (error) {
            if (firebaseUser) await deleteUser(firebaseUser).catch(console.error);
            throw error;
        } finally {
            isSigningUp.current = false;
        }
    };

    const googleLogin = async (recaptchaToken) => {
        if (!auth) throw new Error("Auth not ready");
        const provider = new GoogleAuthProvider();
        
        // Wait for gapi if needed
        await new Promise((resolve) => {
            if (window.gapi) gapi.load('client:auth2', resolve);
            else resolve();
        });

        const userCredential = await signInWithPopup(auth, provider);
        return await fetchAndSetUser(userCredential.user, recaptchaToken);
    };

    const logout = useCallback(async () => {
        try {
            // тнР SECURITY UPGRADE: Tell the backend to block this token immediately
            // We do this first while the token is still in memory/axios headers
            try {
                await api.post('/auth/logout');
                console.log('[AUTH] Backend session revoked successfully.');
            } catch (apiErr) {
                // If the API call fails (e.g. no internet), we still proceed with local logout
                console.warn('[AUTH] Backend logout failed, proceeding with local cleanup.', apiErr.message);
            }

            clearJobLocalState(); 
            resetCsrfToken(); // ⭐ NEW: next login must fetch a fresh CSRF token bound to the new __session

            // 1. Wipe Local State
            // Native: clear the localStorage token. Web: nothing was ever stored
            // there — the cookie is what api.post('/auth/logout') just cleared
            // server-side above.
            setAndStoreUser(null);
            setToken(null);
            if (isNative) localStorage.removeItem('token');
            
            // 2. Sign out of Firebase
            if (auth) await signOut(auth);
        } catch (error) {
            console.error('Logout error:', error);
        }
    }, [auth, clearJobLocalState, setAndStoreUser]);

    const value = { user, token, loading, login, signup, logout, googleLogin, refreshUser, complete2FALogin, updateUser };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};